import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { FaRedo } from "react-icons/fa";
import four from "../../assets/image/four.png";
import one from "../../assets/image/one.png";
import three from "../../assets/image/three.png";
import two from "../../assets/image/two.png";
import "./ContrastModelform.css";
import { postColorChangeBackgroundMessage, postColorChangeTextMessage } from "../../utils/message.helper";

interface ContrastModelProps {
  onClose: () => void;
  children: React.ReactNode;
}

const ContrastModel: React.FC<ContrastModelProps> = ({ onClose, children }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(() => {
    return localStorage.getItem("selectedPreset") || null;
  });
  const [bgColors] = useState([
    "#ff0000",
    "#0000ff",
    "#ffff00",
    "#000000",
    "#e74c3c",
    "conic-gradient(#FF0000, #FFFF00, #00FF00, #0000FF, #000000)",
  ]);
  const [textColors] = useState([
    "#ff0000",
    "#0000ff",
    "#ffff00",
    "#ffffff",
    "#1e8449",
    "#e74c3c",
    "conic-gradient(#FF0000, #FFFF00, #00FF00, #0000FF, #000000)",
  ]);
  const [activeBgColor, setActiveBgColor] = useState<string | null>(() => {
    return localStorage.getItem("activeBgColor") || null;
  });
  const [activeTextColor, setActiveTextColor] = useState<string | null>(() => {
    return localStorage.getItem("activeTextColor") || null;
  });

  const presets: { [key: string]: { bgColor: string; textColor: string } } = {
    one: { bgColor: "#ff0000", textColor: "#1e8449" },
    three: { bgColor: "#ffffff", textColor: "#000000" },
    two: { bgColor: "#0000ff", textColor: "#ffff00" },
    four: { bgColor: "#000000", textColor: "#ffffff" },
  };

  const handlePresetSelection = (preset: string) => {
    setSelectedImage(preset);
    const { bgColor, textColor } = presets[preset];
    setActiveBgColor(bgColor);
    setActiveTextColor(textColor);
    localStorage.setItem("selectedPreset", preset);
    localStorage.setItem("activeBgColor", bgColor);
    localStorage.setItem("activeTextColor", textColor);
    postColorChangeBackgroundMessage(bgColor);
    postColorChangeTextMessage(textColor);
  };

  const handleReset = () => {
    setSelectedImage(null);
    setActiveBgColor(null);
    setActiveTextColor(null);
    localStorage.removeItem("selectedPreset");
    localStorage.removeItem("activeBgColor");
    localStorage.removeItem("activeTextColor");
    postColorChangeBackgroundMessage("");
    postColorChangeTextMessage("");
  };

  const handleBgColorChange = (color: string) => {
    setActiveBgColor(color);
    localStorage.setItem("activeBgColor", color);
    postColorChangeBackgroundMessage(color);
  };

  const handleTextColorChange = (color: string) => {
    setActiveTextColor(color);
    localStorage.setItem("activeTextColor", color);
    postColorChangeTextMessage(color);
  };

  const isGradient = (color: string) => {
    return color.includes("gradient");
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      switch (event.data.action) {
        case "Change Background Color":
          changeBackgroundColor(event.data.color);
          break;
        case "Change Text Color":
          changeTextColor(event.data.color);
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const changeBackgroundColor = (color: string) => {
    const siteText = document.querySelector(".site-text");
    if (siteText) {
      (siteText as HTMLElement).style.backgroundColor = color;
    }
    document.body.style.backgroundColor = color;
  };

  const changeTextColor = (color: string) => {
    const siteText = document.querySelector(".site-text");
    if (siteText) {
      (siteText as HTMLElement).style.color = color;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content rounded">
        {children}
        <div className="flex justify-end px-2 space-x-4 my-4">
          <button
            className="bg-[#dee7ff] text-black px-3 py-1 rounded-3xl flex items-center space-x-2 hover:bg-[#c5d5f7] focus:outline-none focus:ring-2 focus:ring-[#a1c0f0]"
            onClick={handleReset}
          >
            <FaRedo className="w-5 h-5 text-xs bg-white rounded-full p-1" />
            <span className="text-xs">{t("reset")}</span>
          </button>
        </div>
        <div className="gap-4 px-2">
          <h4 className="text-start px-2">{t("select_preset")}</h4>
          <div className="flex justify-between space-x-4 my-4 px-3">
            <button
              className={`py-3 px-3 rounded ${
                selectedImage === "one" ? "bg-blue-500" : "bg-blue-200"
              }`}
              onClick={() => handlePresetSelection("one")}
            >
              <img src={one} alt="" className="w-8 h-8 object-contain" />
            </button>
            <button
              className={`py-3 px-3 rounded ${
                selectedImage === "two" ? "bg-blue-500" : "bg-blue-200"
              }`}
              onClick={() => handlePresetSelection("two")}
            >
              <img src={three} alt="" className="w-8 h-8 object-contain" />
            </button>
            <button
              className={`py-3 px-3 rounded ${
                selectedImage === "three" ? "bg-blue-500" : "bg-blue-200"
              }`}
              onClick={() => handlePresetSelection("three")}
            >
              <img src={two} alt="" className="w-8 h-8 object-contain" />
            </button>
            <button
              className={`py-3 px-3 rounded ${
                selectedImage === "four" ? "bg-blue-500" : "bg-blue-200"
              }`}
              onClick={() => handlePresetSelection("four")}
            >
              <img src={four} alt="" className="w-8 h-8 object-contain" />
            </button>
          </div>

          <h4 className="text-start px-2">{t("background_color")}</h4>
          <div className="flex justify-between my-4 bg-blue-200 py-1 px-2 rounded space-x-2">
            {bgColors.map((color, index) => (
              <div key={index} className="relative">
                <div
                  className={`w-7 h-7 border-2 rounded-full ${
                    activeBgColor === color ? "border-black" : "border-gray-300"
                  }`}
                  style={{
                    background: color,
                  }}
                  onClick={() => handleBgColorChange(color)}
                ></div>
                {isGradient(color) && (
                  <input
                    type="color"
                    onChange={(e) => handleBgColorChange(e.target.value)}
                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                  />
                )}
              </div>
            ))}
          </div>

          <h4 className="text-start px-2">{t("text_color")}</h4>
          <div className="flex justify-between my-4 bg-blue-200 py-1 px-2 rounded space-x-2">
            {textColors.map((color, index) => (
              <div key={index} className="relative">
                <div
                  className={`w-7 h-7 border-2 rounded-full ${
                    activeTextColor === color
                      ? "border-black"
                      : "border-gray-300"
                  }`}
                  style={{
                    background: color,
                  }}
                  onClick={() => handleTextColorChange(color)}
                ></div>
                {isGradient(color) && (
                  <input
                    type="color"
                    onChange={(e) => handleTextColorChange(e.target.value)}
                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                  />
                )}
              </div>
            ))}
          </div>

          <h4 className="text-start px-2">{t("preview")}</h4>
          <div
            className="flex justify-between my-4 py-3 px-2 rounded site-text"
            style={{
              background: activeBgColor || "",
              color: activeTextColor || "",
            }}
          >
            <button style={{ color: activeTextColor || "" }}>
              {t("can_you_read_this_?")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContrastModel;
